import { useEffect } from 'react';
import { useNotices } from '../ui';

/** How often a tab that is being used checks whether it is out of date. */
const EVERY_MS = 5 * 60_000;
/** The entry bundle's name, which changes with every build. */
const ASSET = /\/assets\/index-[A-Za-z0-9._-]+\.js/;

/**
 * The bundle THIS tab booted from, read from the document's own script tag.
 *
 * Null when there is no hashed bundle at all — the dev server serves the source
 * — which is what keeps this quiet outside production instead of prompting a
 * reload every time Vite rebuilds.
 */
function running(): string | null {
  for (const script of document.querySelectorAll('script[type="module"][src]')) {
    const found = ASSET.exec(script.getAttribute('src') ?? '');
    if (found) return found[0];
  }
  return null;
}

/** The bundle the server is handing out right now. */
async function shipped(): Promise<string | null> {
  const response = await fetch('/index.html', { cache: 'no-store' });
  if (!response.ok) return null;
  return ASSET.exec(await response.text())?.[0] ?? null;
}

/**
 * Telling a person their tab is out of date (2026-09-22).
 *
 * A released fix reaches an open tab never: the bundle is cached under a
 * hashed name and nothing asks for a new one until the page is reloaded.
 * Vladimir tested four fixes twenty minutes after they went live, saw none of
 * them, and reported them as still broken — *"I dont see changes, i still cant
 * select so that bot dont join my next meeting with Danil from the row"*. Both
 * halves of that are expensive: he loses the time, and the product loses the
 * report, because "it did not work" and "you are looking at yesterday's app"
 * are indistinguishable from the outside.
 *
 * ⚠ It **asks** rather than reloading. Somebody may be halfway through a note,
 * and a page that reloads itself under a person's hands is worse than a stale
 * one. The notice carries the button and waits.
 */
export function useFreshBuild(): void {
  const { show } = useNotices();

  useEffect(() => {
    const mine = running();
    if (!mine) return;

    let stopped = false;
    const look = (): void => {
      if (stopped || document.hidden) return;
      void shipped()
        .then((current) => {
          if (stopped || !current || current === mine) return;
          show({
            id: 'new-build',
            tone: 'attention',
            text: 'Ptitim has been updated since you opened this page.',
            action: { label: 'Reload', onSelect: () => window.location.reload() },
          });
        })
        // Offline, or the server is mid-release. Ask again next time.
        .catch(() => undefined);
    };

    // On opening, because the tab left open overnight is the common case.
    look();
    const timer = window.setInterval(look, EVERY_MS);
    window.addEventListener('focus', look);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', look);
    };
  }, [show]);
}
